import React, { useState, useEffect } from "react";
import Head from "next/head";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import CountUp from "react-countup";
import classNames from "classnames";
import { WEBPAGE_TITLE } from "@utils/Constants";
import { useGeneralStatistics } from "@hooks/useGeneralStatistics";
import { useInView } from "@hooks/useInView";
import { Leaf } from "@components/Leaf/Leaf";
import REVIEWS from "@utils/Reviews";
import styles from "./FrontPage.module.scss";

const CUSTOMERS = [
    { name: "Hópbílar", logo: "/img/companies/hopbilar.png", id: 216 },
    { name: "Elko", logo: "/img/companies/elko.png", id: 185 },
    { name: "Lopi", logo: "/img/companies/lopi.png", id: 54 },
    { name: "Steypustöðin", logo: "/img/companies/steypustodin.png", id: 5 },
    { name: "Samkaup", logo: "/img/companies/samkaup.png", id: 2 },
];

const SHOWCASE_TABS = [
    { key: "dashboard_biz" },
    { key: "dashboard_muni" },
    { key: "green_steps" },
    { key: "supply_chain" },
    { key: "employees" },
    { key: "energy" },
    { key: "waste" },
    { key: "carbon" },
];

function AnimateIn({ children, className, delay = 0 }) {
    const [ref, isInView] = useInView();
    return (
        <div
            ref={ref}
            className={classNames(
                styles.animate,
                { [styles.visible]: isInView },
                className,
            )}
            style={delay ? { transitionDelay: `${delay}ms` } : undefined}
        >
            {children}
        </div>
    );
}

const PROBLEM_ICONS = {
    "01": (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    ),
    "02": (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
    ),
    "03": (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
    ),
};

const FrontPage = () => {
    const t = useTranslations("FrontPage");
    const { locale } = useRouter();
    const { data: statistics } = useGeneralStatistics();
    const stats = statistics?.results;

    return (
        <>
            <Head>
                <title key="site_title">
                    {WEBPAGE_TITLE + " – Stafrænn sjálfbærnivettvangur"}
                </title>
                <meta
                    property="og:title"
                    content={`${WEBPAGE_TITLE} – Stafrænn sjálfbærnivettvangur`}
                    key="og:title"
                />
            </Head>

            <div className={styles.page}>
                <HeroSection t={t} />
                <LogoStrip t={t} />
                <ProblemsSection t={t} />
                <FeaturesSection t={t} />
                <HowItWorksSection t={t} />
                {stats && <StatsSection t={t} stats={stats} />}
                <TestimonialsSection locale={locale} />
                <PathsSection t={t} />
                <CtaSection t={t} />
            </div>
        </>
    );
};

export default FrontPage;

function HeroSection({ t }) {
    const WORDS = [t("hero_rotate_1"), t("hero_rotate_2"), t("hero_rotate_3")];
    const [wordIdx, setWordIdx] = useState(0);

    useEffect(() => {
        const id = setInterval(
            () => setWordIdx((i) => (i + 1) % WORDS.length),
            3000,
        );
        return () => clearInterval(id);
    }, []);

    return (
        <section className={styles.hero}>
            <div className={styles.heroOrbs} aria-hidden="true">
                <div className={classNames(styles.orb, styles.orb1)} />
                <div className={classNames(styles.orb, styles.orb2)} />
                <div className={classNames(styles.orb, styles.orb3)} />
            </div>

            <Leaf className={styles.heroLeaf1} plain style={{ opacity: 1 }} />
            <Leaf className={styles.heroLeaf2} plain style={{ opacity: 1 }} />

            <div className={styles.heroContent}>
                <div className={styles.heroWordRotator} aria-live="polite">
                    <span key={wordIdx} className={styles.heroWord}>
                        {WORDS[wordIdx]}
                    </span>
                </div>
                <h1 className={styles.heroTitle}>{t("hero_title")}</h1>
                <p className={styles.heroSubtitle}>{t("hero_subtitle")}</p>
                <a
                    className={styles.heroCtaPrimary}
                    href="https://admin.laufid.is/registration"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {t("hero_cta_primary")}
                </a>
            </div>

            <div className={styles.scrollIndicator} aria-hidden="true">
                <div className={styles.scrollMouse}>
                    <div className={styles.scrollDot} />
                </div>
            </div>
        </section>
    );
}

function LogoStrip({ t }) {
    const logos = [...CUSTOMERS, ...CUSTOMERS];
    return (
        <section className={styles.logoStrip}>
            <p className={styles.logoStripLabel}>{t("trusted_by")}</p>
            <div className={styles.logoStripTrack}>
                <div className={styles.logoStripInner}>
                    {logos.map((c, i) => (
                        <img
                            key={`${c.name}-${i}`}
                            src={c.logo}
                            alt={c.name}
                            className={styles.logoStripImg}
                            loading="lazy"
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}

function ProblemsSection({ t }) {
    const problems = [
        {
            num: "01",
            titleKey: "problem_1_title",
            descKey: "problem_1_desc",
            solKey: "solution_1",
        },
        {
            num: "02",
            titleKey: "problem_2_title",
            descKey: "problem_2_desc",
            solKey: "solution_2",
        },
        {
            num: "03",
            titleKey: "problem_3_title",
            descKey: "problem_3_desc",
            solKey: "solution_3",
        },
    ];

    return (
        <section className={styles.problems}>
            <AnimateIn>
                <h2 className={styles.sectionTitle}>{t("problems_title")}</h2>
            </AnimateIn>
            <div className={styles.problemsGrid}>
                {problems.map((p, i) => (
                    <AnimateIn
                        key={p.num}
                        className={styles.problemCard}
                        delay={i * 150}
                    >
                        <div className={styles.problemHeader}>
                            <div className={styles.problemIconCircle}>
                                {PROBLEM_ICONS[p.num]}
                            </div>
                            <span className={styles.problemNum}>{p.num}</span>
                        </div>
                        <h3 className={styles.problemTitle}>{t(p.titleKey)}</h3>
                        <p className={styles.problemDesc}>{t(p.descKey)}</p>
                        <div className={styles.problemSolution}>
                            <span
                                className={styles.problemArrow}
                                aria-hidden="true"
                            >
                                →
                            </span>
                            <p>{t(p.solKey)}</p>
                        </div>
                    </AnimateIn>
                ))}
            </div>
        </section>
    );
}

function FeaturesSection({ t }) {
    const [activeTab, setActiveTab] = useState(0);
    const tab = SHOWCASE_TABS[activeTab];

    return (
        <section className={styles.features}>
            <AnimateIn>
                <h2 className={styles.sectionTitle}>{t("features_title")}</h2>
                <p className={styles.sectionSubtitle}>
                    {t("features_subtitle")}
                </p>
            </AnimateIn>

            <AnimateIn delay={200}>
                <div className={styles.featureTabBar} role="tablist">
                    {SHOWCASE_TABS.map((s, i) => (
                        <button
                            key={s.key}
                            role="tab"
                            aria-selected={i === activeTab}
                            className={classNames(styles.featureTab, {
                                [styles.featureTabActive]: i === activeTab,
                            })}
                            onClick={() => setActiveTab(i)}
                        >
                            {t(`sc_${s.key}_tab`)}
                        </button>
                    ))}
                </div>
            </AnimateIn>

            <AnimateIn delay={300}>
                <div key={tab.key} className={styles.showcaseRow}>
                    <div className={styles.showcaseText}>
                        <span className={styles.showcaseLabel}>
                            {t(`sc_${tab.key}_label`)}
                        </span>
                        <h3 className={styles.showcaseTitle}>
                            {t(`sc_${tab.key}_title`)}
                        </h3>
                        <p className={styles.showcaseDesc}>
                            {t(`sc_${tab.key}_desc`)}
                        </p>
                    </div>
                    <div className={styles.showcaseVisual}>
                        <ShowcaseStats t={t} tabKey={tab.key} />
                    </div>
                </div>
            </AnimateIn>
        </section>
    );
}

function ShowcaseStats({ t, tabKey }) {
    const stats = {
        dashboard_biz: [
            {
                type: "number",
                value: "4+",
                label: "sc_db_stat_views",
                meta: "sc_db_stat_views_meta",
            },
            {
                type: "icon",
                icon: "pulse",
                label: "sc_db_stat_realtime",
                meta: "sc_db_stat_realtime_meta",
            },
            {
                type: "wide",
                icon: "chart",
                label: "sc_db_stat_reports",
                meta: "sc_db_stat_reports_meta",
            },
        ],
        dashboard_muni: [
            {
                type: "number",
                value: "4",
                label: "sc_dm_stat_views",
                meta: "sc_dm_stat_views_meta",
            },
            {
                type: "icon",
                icon: "people",
                label: "sc_dm_stat_institutions",
                meta: "sc_dm_stat_institutions_meta",
            },
            {
                type: "wide",
                icon: "pulse",
                label: "sc_dm_stat_realtime",
                meta: "sc_dm_stat_realtime_meta",
            },
        ],
        green_steps: [
            { type: "number", value: "120+", label: "sc_gs_stat_actions" },
            {
                type: "number",
                value: "3",
                label: "sc_gs_stat_categories",
                meta: "sc_gs_stat_categories_meta",
            },
            { type: "wide", icon: "target", label: "sc_gs_stat_goals" },
        ],
        supply_chain: [
            { type: "icon", icon: "chain", label: "sc_sc_stat_invite" },
            { type: "icon", icon: "chart", label: "sc_sc_stat_esg" },
            { type: "wide", icon: "shield", label: "sc_sc_stat_audit" },
        ],
        employees: [
            { type: "icon", icon: "people", label: "sc_emp_stat_wellbeing" },
            { type: "icon", icon: "pulse", label: "sc_emp_stat_enps" },
            { type: "wide", icon: "target", label: "sc_emp_stat_plans" },
        ],
        energy: [
            { type: "icon", icon: "bolt", label: "sc_en_stat_kwh" },
            { type: "icon", icon: "drop", label: "sc_en_stat_water" },
            { type: "wide", icon: "chart", label: "sc_en_stat_cost" },
        ],
        waste: [
            { type: "icon", icon: "recycle", label: "sc_wa_stat_sorting" },
            { type: "icon", icon: "chart", label: "sc_wa_stat_breakdown" },
            { type: "wide", icon: "target", label: "sc_wa_stat_fenur" },
        ],
        carbon: [
            { type: "icon", icon: "globe", label: "sc_co_stat_scope" },
            { type: "icon", icon: "chart", label: "sc_co_stat_trend" },
            { type: "wide", icon: "doc", label: "sc_co_stat_report" },
        ],
    };

    const ICONS = {
        pulse: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
        ),
        chart: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
            </svg>
        ),
        target: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="6" />
                <circle cx="12" cy="12" r="2" />
            </svg>
        ),
        people: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
        ),
        chain: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
        ),
        shield: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
        ),
        bolt: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
        ),
        drop: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
            </svg>
        ),
        recycle: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <polyline points="1 4 1 10 7 10" />
                <polyline points="23 20 23 14 17 14" />
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
            </svg>
        ),
        globe: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
        ),
        doc: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
        ),
    };

    const items = stats[tabKey] || [];

    return (
        <div className={styles.statGrid}>
            {items.map((s, i) => (
                <div
                    key={i}
                    className={classNames(styles.statCard, {
                        [styles.statCardWide]: s.type === "wide",
                    })}
                >
                    {s.type === "number" && (
                        <span className={styles.statNumber}>{s.value}</span>
                    )}
                    {s.icon && (
                        <div className={styles.statIcon}>{ICONS[s.icon]}</div>
                    )}
                    <span className={styles.statCardLabel}>{t(s.label)}</span>
                    {s.meta && (
                        <span className={styles.statMeta}>{t(s.meta)}</span>
                    )}
                </div>
            ))}
        </div>
    );
}

function HowItWorksSection({ t }) {
    const steps = [
        { num: "1", titleKey: "how_step_1_title", descKey: "how_step_1_desc" },
        { num: "2", titleKey: "how_step_2_title", descKey: "how_step_2_desc" },
        { num: "3", titleKey: "how_step_3_title", descKey: "how_step_3_desc" },
    ];

    return (
        <section className={styles.howItWorks}>
            <AnimateIn>
                <h2 className={styles.sectionTitle}>{t("how_title")}</h2>
            </AnimateIn>
            <div className={styles.howSteps}>
                {steps.map((s, i) => (
                    <AnimateIn
                        key={s.num}
                        className={styles.howStep}
                        delay={i * 200}
                    >
                        <div className={styles.howStepCircle}>
                            <span>{s.num}</span>
                        </div>
                        {i < steps.length - 1 && (
                            <div
                                className={styles.howStepLine}
                                aria-hidden="true"
                            />
                        )}
                        <h3 className={styles.howStepTitle}>{t(s.titleKey)}</h3>
                        <p className={styles.howStepDesc}>{t(s.descKey)}</p>
                    </AnimateIn>
                ))}
            </div>
        </section>
    );
}

function StatsSection({ t, stats }) {
    const STAT_ICONS = {
        users: (
            <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
        ),
        institutions: (
            <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
        ),
        performance: (
            <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
            </svg>
        ),
    };

    return (
        <section className={styles.stats}>
            <div className={styles.statsInner}>
                <StatItem
                    icon={STAT_ICONS.users}
                    value={stats?.users || 0}
                    label={t("users")}
                />
                <StatItem
                    icon={STAT_ICONS.institutions}
                    value={stats?.institutions || 0}
                    label={t("institutions")}
                />
                <StatItem
                    icon={STAT_ICONS.performance}
                    value={stats?.average_green_steps || 0}
                    label={t("average_performance")}
                    suffix="%"
                />
            </div>
        </section>
    );
}

function StatItem({ icon, value, label, suffix = "" }) {
    return (
        <div className={styles.statItem}>
            <div className={styles.statIconCircle}>{icon}</div>
            <CountUp
                end={value}
                suffix={suffix}
                duration={2.5}
                separator="."
                enableScrollSpy
                scrollSpyOnce
                scrollSpyDelay={200}
                className={styles.statValue}
            />
            <span className={styles.statLabel}>{label}</span>
        </div>
    );
}

function TestimonialsSection({ locale }) {
    const [active, setActive] = useState(0);

    useEffect(() => {
        const id = setInterval(
            () => setActive((i) => (i + 1) % REVIEWS.length),
            8000,
        );
        return () => clearInterval(id);
    }, []);

    return (
        <section className={styles.testimonials}>
            <div className={styles.testimonialSlider}>
                {REVIEWS.map((r, i) => (
                    <blockquote
                        key={i}
                        className={classNames(styles.testimonialCard, {
                            [styles.testimonialActive]: i === active,
                        })}
                    >
                        <div className={styles.testimonialInner}>
                            <span
                                className={styles.testimonialQuote}
                                aria-hidden="true"
                            >
                                &ldquo;
                            </span>
                            <p className={styles.testimonialText}>
                                {locale === "en" ? r.review_en : r.review_is}
                            </p>
                            <span
                                className={styles.testimonialQuoteEnd}
                                aria-hidden="true"
                            >
                                &rdquo;
                            </span>
                            {r.reviewerName && (
                                <footer className={styles.testimonialAuthor}>
                                    <div className={styles.testimonialAvatar}>
                                        {r.reviewerName.charAt(0)}
                                    </div>
                                    <div className={styles.testimonialMeta}>
                                        <strong>{r.reviewerName}</strong>
                                        {(r.reviewerTitle_is ||
                                            r.reviewerTitle_en) && (
                                            <span>
                                                {locale === "en"
                                                    ? r.reviewerTitle_en
                                                    : r.reviewerTitle_is}
                                            </span>
                                        )}
                                    </div>
                                </footer>
                            )}
                        </div>
                    </blockquote>
                ))}
            </div>
            <div className={styles.testimonialDots} role="tablist">
                {REVIEWS.map((_, i) => (
                    <button
                        key={i}
                        className={classNames(styles.testimonialDot, {
                            [styles.dotActive]: i === active,
                        })}
                        onClick={() => setActive(i)}
                        role="tab"
                        aria-selected={i === active}
                        aria-label={`Testimonial ${i + 1}`}
                    />
                ))}
            </div>
        </section>
    );
}

function PathsSection({ t }) {
    return (
        <section className={styles.paths}>
            <AnimateIn>
                <div className={styles.pathsGrid}>
                    <PathCard
                        accent="green"
                        icon={
                            <svg
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <rect
                                    x="2"
                                    y="7"
                                    width="20"
                                    height="14"
                                    rx="2"
                                    ry="2"
                                />
                                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                            </svg>
                        }
                        title={t("path_companies_title")}
                        desc={t("path_companies_desc")}
                        items={[
                            t("measurable_informative_actions"),
                            t("facilitates_information_provision"),
                            t("leading_goal_setting"),
                            t("increased_competitiveness"),
                        ]}
                        ctaText={t("path_companies_cta")}
                        ctaHref="/lausnir/fyrirtaeki"
                    />
                    <PathCard
                        accent="teal"
                        icon={
                            <svg
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                <polyline points="9 22 9 12 15 12 15 22" />
                            </svg>
                        }
                        title={t("path_institutions_title")}
                        desc={t("path_institutions_desc")}
                        items={[
                            t("oversight_institutional_sustainability"),
                            t("support_achieve_set_goals"),
                            t("important_comparison"),
                            t("increased_efficiency_empowerment"),
                        ]}
                        ctaText={t("path_institutions_cta")}
                        ctaHref="/lausnir/opinberir-adilar"
                    />
                </div>
            </AnimateIn>
        </section>
    );
}

function PathCard({ accent, icon, title, desc, items, ctaText, ctaHref }) {
    return (
        <div
            className={classNames(
                styles.pathCard,
                styles[`pathCard_${accent}`],
            )}
        >
            <div className={styles.pathCardIcon}>{icon}</div>
            <h3 className={styles.pathCardTitle}>{title}</h3>
            <p className={styles.pathCardDesc}>{desc}</p>
            <ul className={styles.pathCardList}>
                {items.map((item) => (
                    <li key={item}>{item}</li>
                ))}
            </ul>
            <Link href={ctaHref} className={styles.pathCardCta}>
                {ctaText} →
            </Link>
        </div>
    );
}

function CtaSection({ t }) {
    return (
        <section className={styles.finalCta}>
            <AnimateIn>
                <div className={styles.ctaCard}>
                    <h2 className={styles.ctaTitle}>{t("final_cta_title")}</h2>
                    <p className={styles.ctaDesc}>{t("final_cta_desc")}</p>
                    <a
                        className={styles.ctaButton}
                        href="https://admin.laufid.is/registration"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t("final_cta_button")}
                    </a>
                </div>
            </AnimateIn>
        </section>
    );
}
